import { useEffect, useMemo, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { ISurgeOverlap, ISurgePreviewRow } from "@/api_framework/api_modals/SurgePricing";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import Input from "@/app/components/ui/Inputs/Input";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import Toggle from "@/app/components/ui/Toggles";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import { useSurgePricingAPI } from "@/app/hooks/api_hooks/surge_pricing/useSurgePricingAPI";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useRouter } from "@/app/hooks/useRouter";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { FormRow, InlineField, PageHeader, SectionCard, SubPanel } from "./components";
import ItemPicker from "./ItemPicker";
import ItemScopeSection from "./ItemScopeSection";
import PersonaSection from "./PersonaSection";
import {
  CLINIC_SCOPE_OPTIONS,
  clinicsInCities,
  DAY_ITEMS,
  DAYS_MODE_ITEMS,
  EMPTY_RULE_FORM,
  IScopeForm,
  ISurgeRuleForm,
  ITEM_TYPE_ITEMS,
  pickItem,
  pickItems,
  ruleSummary,
  TIME_MODE_ITEMS,
  toRuleBody,
  toRuleForm,
  useSurgeLookups,
} from "./interface";

const DATE_FORMAT = "yyyy-MM-dd";
const tableCellClassName = "text-xs font-medium text-black whitespace-normal py-3 px-4";
const tableHeaderClassName = "text-xs font-semibold text-[#193A6A] py-3 px-4";



const previewColumns: TableColumn<ISurgePreviewRow>[] = [
  { id: "name", header: "Item", className: tableCellClassName, headerClassName: tableHeaderClassName, cell: ({ row }) => row.name ?? row.item_id },
  { id: "clinic", header: "Clinic Price", className: tableCellClassName, headerClassName: tableHeaderClassName, cell: ({ row }) => `₹${row.clinic_price}` },
  {
    id: "surge",
    header: "Surge Price",
    className: `${tableCellClassName} font-semibold`,
    headerClassName: tableHeaderClassName,
    cell: ({ row }) => (row.uplifted_price == null ? "Not covered" : `₹${row.uplifted_price}`),
  },
];

const toMinutes = (h: string, m: string) => Number(h) * 60 + Number(m);
const NO_IDS: string[] = [];
// Emoji / pictographs (same rule as the API).
const EMOJI = /\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]|\uFE0F|\u200D|\u20E3/u;
const noEmoji = (v?: string) => !v || !EMOJI.test(v);

const FIELD_LABELS: Record<string, string> = {
  name: "Rule Name",
  reason: "Reason",
  service_type_ids: "Appointment Types",
  item_types: "Item Types",
  uplift_value: "Percent",
  valid_from: "Valid from",
  days_mode: "Days",
  time_mode: "Time",
  persona_included: "Personas to include",
  persona_excluded: "Personas to exclude",
};

const SurgeRuleOnboard = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const [params] = useSearchParams();
  const ruleId = params.get("id") ?? undefined;
  const arrayConstants = useArrayConstants();
  const hourItems = arrayConstants.overBookingStartTimeItems as ISelectMenuItemData[];
  const minuteItems = arrayConstants.overBookingStartTimeMinuteItems as ISelectMenuItemData[];
  const { getRule, saveRule, previewRule, listRules, isLoading } = useSurgePricingAPI();
  const { execute: fetchCategories, serviceCategory } = useCouponCategoryAPI();
  const { cityItems, clinicItems, clinicCity, appointmentTypeItems, personas } = useSurgeLookups();

  const [isLoaded, setIsLoaded] = useState<boolean>(!ruleId);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [preview, setPreview] = useState<ISurgePreviewRow[]>([]);
  const [overlaps, setOverlaps] = useState<ISurgeOverlap[]>([]);

  const formHook = useForm<ISurgeRuleForm>({ mode: "onChange", defaultValues: EMPTY_RULE_FORM });
  const { register, setValue, watch, formState } = formHook;
  const errors = formState.errors;
  const disabled = !canEdit;

  useEffect(() => {
    listRules({ page: 1, page_size: 1 }).then((data) => setCanEdit(Boolean(data?.can_edit)));
    fetchCategories();
  }, [listRules, fetchCategories]);

  const categoryItems = useMemo(
    () => serviceCategory.map((c) => ({ id: String(c.id), title: c.category_name })),
    [serviceCategory],
  );

  useEffect(() => {
    if (!ruleId) return;
    getRule(ruleId).then((data) => {
      if (!data) return;
      formHook.reset(toRuleForm(data));
      setOverlaps(data.overlaps_with ?? []);
      setIsLoaded(true);
    });
  }, [ruleId, getRule]);

  const [startH, startM, endH, endM] = watch(["start_h", "start_m", "end_h", "end_m"]);
  // An end before the start runs into the next day (10 PM to 2 AM).
  const endsNextDay = !!(startH && startM && endH && endM) && toMinutes(endH, endM) < toMinutes(startH, startM);

  const onSubmit = async (form: ISurgeRuleForm) => {
    if (form.clinic_scope === "CLINICS" && !form.applicable_clinics.length) return toast.error("Select at least one clinic.");
    if (!form.item_types.length) return toast.error("Select Services, BOMs or both.");
    const incomplete = form.item_types.find((type) => {
      const scope = type === "SERVICE" ? form.service_scope : form.bom_scope;
      return !(scope.category.length && scope.sub_category.length && scope.items.length);
    });
    if (incomplete) {
      return toast.error(`${incomplete === "SERVICE" ? "Services" : "BOMs"}: pick category, sub category and items.`);
    }
    if (form.persona_mode === "inactive" && !form.persona_included.length) return toast.error("Select at least one persona.");
    if (form.exclude_enabled && !form.persona_excluded.length) return toast.error("Select personas to exclude.");
    if (form.days_mode === "CUSTOM" && !form.custom_days.length) return toast.error("Pick at least one day.");
    if (form.time_mode === "CUSTOM" && !(form.start_h && form.start_m && form.end_h && form.end_m)) {
      return toast.error("Select start and end time.");
    }
    if (form.time_mode === "CUSTOM" && toMinutes(form.end_h, form.end_m) === toMinutes(form.start_h, form.start_m)) {
      return toast.error("Start and end time can't be the same.");
    }
    const saved = await saveRule(toRuleBody(form), ruleId);
    if (!saved) return;
    toast.success(ruleId ? "Rule updated." : "Rule created.");
    if (saved.overlaps_with?.length) {
      toast(`Overlaps with ${saved.overlaps_with.map((o) => o.name).join(", ")}. The rule giving the higher total wins.`, {
        icon: "⚠️",
      });
    }
    // Back to the list, like the other Inventory Setup forms.
    navigate(getRouteKey("SURGE_PRICING_LIST", "url"));
  };

  // Never fail silently: name what blocked the save.
  const onInvalid = (invalid: FieldErrors<ISurgeRuleForm>) => {
    const names = Object.keys(invalid).map((key) => FIELD_LABELS[key] ?? key);
    toast.error(`Please fill: ${names.join(", ")}`);
  };

  const onPreview = async () => {
    const form = formHook.getValues();
    if (!form.sample_clinic_id) return toast.error("Select a clinic for the preview.");
    if (!form.preview_items.length) return toast.error("Add sample items to preview.");
    const data = await previewRule(toRuleBody(form), form.sample_clinic_id, form.preview_items);
    if (!data) return;
    setPreview(data.items);
    setOverlaps(data.overlaps_with);
    if (!data.items.length) toast("None of these items are priced at this clinic.");
  };

  const multiSelect = (name: keyof ISurgeRuleForm, items: ISelectMenuItemData[], placeholder: string, required = false) => (
    <SearchableMultiSelectMenu
      onSelectItem={(selected) => setValue(name, (selected ?? []).map((i) => i.id) as never)}
      errorMessages={[{ message: `${placeholder.replace("Select ", "")} is required`, type: "validate" }]}
      fieldError={errors[name] as never}
      register={register(name, { validate: (v) => !required || (Array.isArray(v) && v.length > 0) })}
      withSelectAll
      showTooltips={true}
      showTypedErrors={false}
      showDropdownIcon={true}
      placeholder={placeholder}
      defaultSelected={pickItems(items, watch(name) as string[])}
      // Without ids the menu clears its picks when its options are already loaded (edit page).
      defaultSelectedIds={(watch(name) as string[]) ?? NO_IDS}
      selectItems={items}
      disabled={disabled}
      listBoxClassName="w-full"
      className="text-gray-800"
      containerClassName="w-full"
    />
  );

  const singleSelect = (name: keyof ISurgeRuleForm, items: ISelectMenuItemData[], placeholder: string) => (
    <NonSearchableSelectMenu
      errorMessages={[{ type: "required", message: `${placeholder} is required` }]}
      onSelectItem={(item) => item && setValue(name, item.id as never, { shouldDirty: true })}
      fieldError={errors[name] as never}
      register={register(name, { required: true })}
      selectItems={items}
      placeholder={placeholder}
      showTooltips={false}
      showTypedErrors={false}
      showDropdownIcon={true}
      disabled={disabled}
      defaultSelected={pickItem(items, watch(name) as string)}
      listBoxClassName="w-full"
      className="text-gray-800"
      containerClassName="w-full"
    />
  );

  const timeSelect = (name: "start_h" | "start_m" | "end_h" | "end_m", items: ISelectMenuItemData[], placeholder: string) => (
    <div className="w-24 flex-shrink-0">
      <NonSearchableSelectMenu
        errorMessages={[]}
        onSelectItem={(item) => item && setValue(name, item.title)}
        fieldError={errors[name]}
        register={register(name)}
        selectItems={items}
        placeholder={placeholder}
        showTooltips={false}
        showTypedErrors={false}
        showDropdownIcon={true}
        disabled={disabled}
        defaultSelected={items.find((i) => i.title === watch(name))}
        listBoxClassName="w-full"
        className="text-gray-800"
      />
    </div>
  );

  const dateField = (name: "valid_from" | "valid_till", required: boolean) => (
    <div className="w-40 flex-shrink-0">
      <PWLCalendarComponent
        disableDialogScrollLock
        showFutureYears
        withClear={!required}
        disabled={disabled}
        errorMessages={[{ message: "Date is required", type: "required" }]}
        register={register(name, { required })}
        fieldError={errors[name]}
        onSelectDate={(date) => setValue(name, date ? format(date, DATE_FORMAT) : "")}
        defaultSelectedDate={watch(name)}
        className="w-full"
      />
    </div>
  );

  const setScope = (key: "service_scope" | "bom_scope", value: IScopeForm) => setValue(key, value, { shouldDirty: true });

  if (!isLoaded) return <SkeletonType1 />;

  const summary = ruleSummary(watch(), { cityItems, clinicItems, appointmentTypeItems });
  const clinicOptions = clinicsInCities(clinicItems, clinicCity, watch("applicable_city"));
  const fieldClass = "border-slate-400 rounded-md w-full h-9 text-sm";

  return (
    <div className="flex flex-col gap-3 pb-24">
      <PageHeader
        title={ruleId ? "Edit Surge Rule" : "Add New Surge Rule"}
        subtitle={<span className="text-gray-700">{summary}</span>}
        actions={
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-700">{watch("status") === "ACTIVE" ? "Active" : "Inactive"}</span>
            <Toggle
              checked={watch("status") === "ACTIVE"}
              disabled={disabled}
              onToggle={(state) => setValue("status", state ? "ACTIVE" : "INACTIVE")}
            />
            {disabled && <SimpleBadge label="View only" colorClassName="text-gray-600 bg-gray-100 ring-gray-500/10" />}
          </div>
        }
      >
        {overlaps.length > 0 && (
          <p className="mt-3 rounded-md bg-[#FEF9C3] px-3 py-2 text-xs font-medium text-[#854D0E]">
            Overlaps with {overlaps.map((o) => o.name).join(", ")}. The rule giving the higher total wins.
          </p>
        )}
      </PageHeader>

      <form className="flex w-full flex-col gap-3" onSubmit={formHook.handleSubmit(onSubmit, onInvalid)}>
        <SectionCard title="Basic Details" description="Name the rule and note why it exists (the reason is kept in history).">
          <FormRow label="Rule Name" required>
            <Input
              register={register("name", { required: true, validate: noEmoji })}
              type="text"
              fieldError={errors.name}
              errorMessages={[
                { type: "required", message: "Rule Name is required" },
                { type: "validate", message: "Emojis are not allowed" },
              ]}
              className={fieldClass}
              inputClassName="h-9"
              placeholder="e.g. Weekend At Home BOMs"
              disabled={disabled}
            />
          </FormRow>
          <FormRow label="Reason">
            <Input
              register={register("reason", { validate: noEmoji })}
              type="text"
              fieldError={errors.reason}
              errorMessages={[{ type: "validate", message: "Emojis are not allowed" }]}
              className={fieldClass}
              inputClassName="h-9"
              placeholder="Why this rule"
              disabled={disabled}
            />
          </FormRow>
        </SectionCard>

        <SectionCard title="Appointment & Location" description="Visits this rule applies to. Other visits pay the clinic price.">
          <FormRow label="Appointment Types" required>
            {multiSelect("service_type_ids", appointmentTypeItems, "Select Appointment Types", true)}
          </FormRow>
          <FormRow label="Cities" hint="Leave empty for all cities.">
            {multiSelect("applicable_city", cityItems, "All Cities")}
          </FormRow>
          <FormRow label="Clinics">
            <RadioGroupComponent
              alignment="horizontal"
              groupOptions={CLINIC_SCOPE_OPTIONS}
              defaultSelectedId={watch("clinic_scope")}
              disabled={disabled}
              onRadioChange={(id) => setValue("clinic_scope", id)}
              register={register("clinic_scope")}
              errorMessages={[]}
              fieldError={undefined}
              radioOptionClassName="text-sm text-gray-700 font-medium"
            />
            {watch("clinic_scope") === "CLINICS" && (
              <SubPanel>{multiSelect("applicable_clinics", clinicOptions, "Select Clinics")}</SubPanel>
            )}
          </FormRow>
        </SectionCard>

        <SectionCard title="Customers" description="Pick personas to limit the rule; a customer needs any one of them.">
          <PersonaSection formHook={formHook} personas={personas} disabled={disabled} />
        </SectionCard>

        <SectionCard
          title="Items"
          description="Like coupons: a line needs its category, sub category and item all selected. Products are never increased."
        >
          <FormRow label="Item Types" required>
            {multiSelect("item_types", ITEM_TYPE_ITEMS, "Select Services / BOMs", true)}
          </FormRow>
          <FormRow label="Exclusions" hint="Anything in an excluded category, sub category or item list never gets the increase.">
            <Toggle
              checked={watch("items_exclude_enabled")}
              disabled={disabled}
              onToggle={(state) => setValue("items_exclude_enabled", state, { shouldDirty: true })}
            />
          </FormRow>
          {watch("item_types").includes("SERVICE") && (
            <ItemScopeSection
              type="SERVICE"
              value={watch("service_scope")}
              onChange={(value) => setScope("service_scope", value)}
              categoryItems={categoryItems}
              showExclude={watch("items_exclude_enabled")}
              disabled={disabled}
            />
          )}
          {watch("item_types").includes("BOM") && (
            <ItemScopeSection
              type="BOM"
              value={watch("bom_scope")}
              onChange={(value) => setScope("bom_scope", value)}
              categoryItems={categoryItems}
              showExclude={watch("items_exclude_enabled")}
              disabled={disabled}
            />
          )}
        </SectionCard>

        <SectionCard title="Price Increase" description="Percent added to the clinic price, then rounded up to end in 9 (543 → 549).">
          <FormRow label="Increase" required>
            <div className="sm:w-36">
              <InlineField label="Percent (%)" required>
                <Input
                  register={register("uplift_value", {
                    required: true,
                    min: 1,
                    max: 500,
                    validate: (v) => Number.isInteger(Number(v)) || "Whole numbers only",
                  })}
                  type="number"
                  fieldError={errors.uplift_value}
                  errorMessages={[
                    { type: "required", message: "Value is required" },
                    { type: "min", message: "Must be at least 1" },
                    { type: "validate", message: "Whole numbers only" },
                    { type: "max", message: "Can't be more than 500" },
                  ]}
                  className={fieldClass}
                  inputClassName="h-9"
                  placeholder="e.g. 20"
                  disabled={disabled}
                />
              </InlineField>
            </div>
          </FormRow>
        </SectionCard>

        <SectionCard title="Schedule" description="Applies when the booked slot (start to end) overlaps this window, for both the booking quote and the clinic bill. Start and end times both count, so 1–2 PM includes 2:00.">
          <FormRow label="Valid" required>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <InlineField label="From" required>
                {dateField("valid_from", true)}
              </InlineField>
              <InlineField label="Till (optional)">{dateField("valid_till", false)}</InlineField>
            </div>
          </FormRow>
          <FormRow label="Days & Time" required>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="sm:w-56">
                <InlineField label="Days">{singleSelect("days_mode", DAYS_MODE_ITEMS, "Days")}</InlineField>
              </div>
              <div className="sm:w-56">
                <InlineField label="Time">{singleSelect("time_mode", TIME_MODE_ITEMS, "Time")}</InlineField>
              </div>
            </div>
            {watch("days_mode") === "CUSTOM" && (
              <SubPanel>
                <InlineField label="Pick days" required>
                  {multiSelect("custom_days", DAY_ITEMS, "Select Days")}
                </InlineField>
              </SubPanel>
            )}
            {watch("time_mode") === "CUSTOM" && (
              <SubPanel>
                <InlineField label="Hours" required>
                  <div className="flex flex-wrap items-center gap-2">
                    {timeSelect("start_h", hourItems, "HH")}
                    {timeSelect("start_m", minuteItems, "MM")}
                    <span className="text-xs text-gray-500">to</span>
                    {timeSelect("end_h", hourItems, "HH")}
                    {timeSelect("end_m", minuteItems, "MM")}
                    {endsNextDay && <span className="text-xs text-gray-500">(next day)</span>}
                  </div>
                </InlineField>
              </SubPanel>
            )}
          </FormRow>
        </SectionCard>

        <SectionCard title="Preview" description="See clinic price vs increased price at one clinic (prices differ per clinic).">
          <FormRow label="Preview at Clinic">
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => item && setValue("sample_clinic_id", item.id)}
              fieldError={errors.sample_clinic_id}
              register={register("sample_clinic_id")}
              selectItems={clinicItems}
              placeholder="Select Clinic"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={pickItem(clinicItems, watch("sample_clinic_id"))}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </FormRow>
          <FormRow label="Sample Items">
            <ItemPicker
              clinicId={watch("sample_clinic_id")}
              selected={watch("preview_items")}
              onChange={(list) => setValue("preview_items", list)}
              register={register("preview_items")}
            />
          </FormRow>
          <div className="py-2">
            <button type="button" className={SECONDARY_BUTTON} onClick={onPreview} disabled={isLoading}>
              Preview Prices
            </button>
          </div>
          {preview.length > 0 && (
            <TableComponent<ISurgePreviewRow>
              columns={previewColumns}
              data={preview}
              getRowId={(row) => row.item_id}
              containerClassName="border border-gray-200/80 shadow-sm rounded-2xl mr-0 bg-white"
              tableClassName="min-w-full divide-y divide-gray-200"
              wrapCells={true}
            />
          )}
        </SectionCard>

        <div className="fixed inset-x-0 bottom-0 z-20 flex w-full items-center justify-end gap-4 border-t-2 bg-white p-4">
          <button
            type="button"
            onClick={() => navigate(getRouteKey("SURGE_PRICING_LIST", "url"))}
            className={`${SECONDARY_BUTTON} flex-1 sm:flex-none`}
          >
            Back
          </button>
          {canEdit && (
            <button type="submit" className={`${PRIMARY_BUTTON} flex-1 sm:flex-none`} disabled={isLoading}>
              Save
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default SurgeRuleOnboard;
